import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ManagerDashboard.css";
import { useDispatch, useSelector } from 'react-redux';
import { selectAdminToken } from "../../admin/sessionSlice";
import { getManagerSettings } from "../../functions/getManagerSettings";
import { addManagerQuestions } from "../../functions/addManagerQuestions";
import { updateGameSettings } from "../../functions/updateGameSettings";
import { getLobbyGameData } from "../../functions/getLobbyGameData";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import { setBackButtonUrl } from '../uiSlice';


// ── Extramile URLs from environment ──────────────────────────────────────────
const EXTRAMILE_GUEST_JOIN = process.env.REACT_APP_EXTRAMILE_GUEST_JOIN_URL || "";
const EXTRAMILE_SIGNUP = process.env.REACT_APP_EXTRAMILE_SIGNUP_URL || "";

function buildExtramileJoinUrl(isGuest, gameId, sessionId, organizationId) {
  const rawBase = isGuest ? EXTRAMILE_GUEST_JOIN : EXTRAMILE_SIGNUP;
  const base = String(rawBase || "").replace(/\/$/, "");
  if (!base) return "";
  const u = new URL(base);
  u.searchParams.set("gameId", String(gameId ?? ""));
  u.searchParams.set("sessionId", String(sessionId ?? ""));
  u.searchParams.set("organizationId", String(organizationId ?? ""));
  return u.toString();
}

function appendLobbyShareFlagsToJoinUrl(baseUrl, lobbyId) {
  const base = String(baseUrl ?? "").trim();
  const id = String(lobbyId ?? "").trim();
  if (!base) return "";
  if (!id) return base;
  try {
    const u = new URL(base);
    u.searchParams.set("lobby", id);
    return u.toString();
  } catch {
    return base;
  }
}

function deriveGuestFromUser(u) {
  const raw = String(u?.user_type ?? u?.userType ?? u?.role ?? u?.source ?? "").trim();
  const ut = raw.toUpperCase().replace(/\s+/g, "_").replace(/-+/g, "_");
  if (ut === "ORG_USER" || ut === "ORG" || ut === "ORGUSER" || ut.startsWith("ORG_")) return false;
  if (ut === "GUEST_USER" || ut === "GUEST" || ut.startsWith("GUEST_")) return true;
  return true;
}

function buildFallbackAppLink(lobbyName) {
  const base = String(process.env.REACT_APP_BASE_URL || window.location.origin || "").replace(/\/+$/, "");
  const l = encodeURIComponent(String(lobbyName || ""));
  if (!base) return "";
  if (!l) return `${base}/login`;
  return `${base}/login?lobby=${l}`;
}

function withQuestionAmp(url) {
  const s = String(url || "");
  return s.includes("?") ? s.replace("?", "?&") : s;
}

// ── Responsive helper ─────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 700);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 700);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page: (isMobile) => ({
    minHeight: "90vh",
    fontFamily: "'Nunito', sans-serif",
    padding: isMobile ? "20px 14px 80px" : "36px 24px 80px",
    position: "relative",
    overflow: "hidden",
  }),
  bgBlob1: {
    position: "fixed",
    top: -60,
    left: -60,
    width: 220,
    height: 220,
    background: "rgba(99,162,255,0.18)",
    borderRadius: "50%",
    pointerEvents: "none",
    zIndex: 0,
  },
  bgBlob2: {
    position: "fixed",
    bottom: 40,
    left: 20,
    width: 160,
    height: 160,
    background: "rgba(150,130,255,0.13)",
    borderRadius: "50%",
    pointerEvents: "none",
    zIndex: 0,
  },
  qmarks: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    width: 80,
    display: "flex",
    flexDirection: "column",
    gap: 60,
    justifyContent: "center",
    alignItems: "center",
    pointerEvents: "none",
    zIndex: 0,
  },
  qmark: { fontSize: 38, fontWeight: 900, color: "rgba(120,140,210,0.12)" },
  inner: { maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 1 },
  title: (isMobile) => ({
    textAlign: "center",
    fontSize: isMobile ? 26 : 44,
    fontWeight: 900,
    color: "#3b82f6",
    marginBottom: isMobile ? 18 : 30,
    letterSpacing: -1,
    textShadow: "0 2px 20px rgba(59,130,246,0.15)",
    fontFamily: "'Nunito', sans-serif",
  }),
  topRow: (isMobile) => ({
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? 14 : 20,
    marginBottom: isMobile ? 18 : 28,
  }),
  card: (isMobile) => ({
    background: "rgba(255,255,255,0.78)",
    backdropFilter: "blur(18px)",
    border: "1.5px solid rgba(255,255,255,0.9)",
    borderRadius: isMobile ? 16 : 20,
    padding: isMobile ? "18px 16px" : "24px 28px",
    boxShadow: "0 6px 28px rgba(100,120,200,0.09)",
  }),
  cardTitle: (isMobile) => ({
    fontSize: isMobile ? 16 : 20,
    fontWeight: 800,
    color: "#1a2b5e",
    marginBottom: isMobile ? 12 : 16,
  }),
  genCard: (isMobile) => (isMobile ? {} : { flex: "0 0 440px" }),
  settCard: (isMobile) => (isMobile ? {} : { flex: 1 }),
  topicInput: (isMobile) => ({
    width: "100%",
    padding: isMobile ? "11px 16px" : "12px 18px",
    borderRadius: 50,
    border: "1.5px solid #d4dcf8",
    background: "#f4f6fd",
    fontSize: isMobile ? 15 : 15,
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 600,
    color: "#1a2b5e",
    outline: "none",
    marginBottom: 12,
    boxSizing: "border-box",
  }),
  genBtn: (isMobile) => ({
    width: "100%",
    padding: isMobile ? "12px 0" : "13px 0",
    borderRadius: 50,
    background: "linear-gradient(135deg,#4f9ef8,#2563eb)",
    color: "white",
    border: "none",
    fontSize: isMobile ? 15 : 16,
    fontWeight: 800,
    fontFamily: "'Nunito', sans-serif",
    cursor: "pointer",
    letterSpacing: 0.5,
    boxShadow: "0 6px 20px rgba(37,99,235,0.35)",
    transition: "opacity 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  }),
  checkRow: { display: "flex", alignItems: "center", gap: 10, cursor: "pointer" },
  checkBox: (checked) => ({
    width: 22,
    height: 22,
    borderRadius: 6,
    background: checked ? "#2563eb" : "#f0f2fa",
    border: `2px solid ${checked ? "#2563eb" : "#c8d0e8"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "all 0.15s",
  }),
  checkLabel: { fontSize: 16, fontWeight: 700, color: "#1a2b5e" },
  sectionLabel: (isMobile) => ({
    fontSize: isMobile ? 16 : 20,
    fontWeight: 800,
    color: "#1a2b5e",
    marginBottom: isMobile ? 10 : 14,
  }),
  dropdownCard: (isMobile) => ({
    background: "rgba(255,255,255,0.9)",
    backdropFilter: "blur(18px)",
    border: "1.5px solid rgba(255,255,255,0.9)",
    borderRadius: isMobile ? 14 : 16,
    boxShadow: "0 6px 28px rgba(100,120,200,0.12)",
    marginBottom: 10,
    overflow: "hidden",
  }),
  dropdownHeader: (isMobile) => ({
    display: "flex",
    alignItems: "center",
    gap: isMobile ? 10 : 14,
    padding: isMobile ? "12px 14px" : "16px 20px",
    cursor: "pointer",
    userSelect: "none",
  }),
  qBadge: (isMobile) => ({
    width: isMobile ? 36 : 42,
    height: isMobile ? 36 : 42,
    borderRadius: 12,
    background: "linear-gradient(135deg,#dce8fc,#cdd8fa)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: isMobile ? 12 : 14,
    fontWeight: 800,
    color: "#2563eb",
    flexShrink: 0,
  }),
  qTitle: (isMobile) => ({ fontWeight: 800, fontSize: isMobile ? 14 : 16, color: "#1a2b5e" }),
  qSub: { fontSize: 13, color: "#6b7db3", fontWeight: 600, marginTop: 2 },
  qActions: { marginLeft: "auto", display: "flex", gap: 8 },
  iconBtn: (color, active = false) => ({
    width: 34,
    height: 34,
    borderRadius: 8,
    border: "none",
    background: active ? "rgba(37,99,235,0.16)" : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: active ? "#2563eb" : color,
    fontSize: 16,
    boxShadow: active ? "0 0 0 1px rgba(37,99,235,0.28) inset" : "none",
    transition: "background 0.15s, color 0.15s, box-shadow 0.15s",
  }),
  expandBody: {
    padding: "0 20px 20px",
    borderTop: "1px solid #edf0fa",
    animation: "fadeIn 0.2s ease",
  },
  expandBodyMobile: {
    padding: "0 14px 16px",
    borderTop: "1px solid #edf0fa",
    animation: "fadeIn 0.2s ease",
  },
  optionsGrid: (isMobile) => ({
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    gap: isMobile ? 8 : 10,
    marginTop: 14,
  }),
  optionRow: { display: "flex", alignItems: "center", gap: 8 },
  optLabel: {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: "#eef2ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 800,
    color: "#2563eb",
    flexShrink: 0,
  },
  optInput: {
    flex: 1,
    padding: "9px 14px",
    borderRadius: 10,
    border: "1.5px solid #d4dcf8",
    background: "#f8f9fe",
    fontSize: 14,
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 600,
    color: "#1a2b5e",
    outline: "none",
  },
  qInput: (isMobile) => ({
    width: "100%",
    padding: "10px 14px",
    borderRadius: 10,
    border: "1.5px solid #d4dcf8",
    background: "#f8f9fe",
    fontSize: isMobile ? 14 : 14,
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 700,
    color: "#1a2b5e",
    outline: "none",
    marginTop: 14,
    boxSizing: "border-box",
  }),
  addNewBtn: (isMobile) => ({
    width: "100%",
    padding: isMobile ? "14px 0" : "18px 0",
    borderRadius: 16,
    border: "2px dashed #b8c8f0",
    background: "rgba(255,255,255,0.5)",
    fontSize: isMobile ? 14 : 16,
    fontWeight: 800,
    color: "#6b7db3",
    fontFamily: "'Nunito', sans-serif",
    cursor: "pointer",
    marginBottom: 20,
    transition: "all 0.15s",
  }),
  startBtn: (isMobile) => ({
    width: "100%",
    padding: isMobile ? "16px 0" : "20px 0",
    borderRadius: 50,
    background: "linear-gradient(135deg,#4f9ef8,#2563eb)",
    color: "white",
    border: "none",
    fontSize: isMobile ? 17 : 20,
    fontWeight: 900,
    fontFamily: "'Nunito', sans-serif",
    cursor: "pointer",
    letterSpacing: 0.5,
    boxShadow: "0 8px 28px rgba(37,99,235,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  }),
  fab: {
    position: "fixed",
    bottom: 30,
    right: 24,
    width: 54,
    height: 54,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#4f9ef8,#2563eb)",
    color: "white",
    border: "none",
    fontSize: 28,
    fontWeight: 700,
    cursor: "pointer",
    zIndex: 100,
    boxShadow: "0 6px 20px rgba(37,99,235,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  aiDropdown: {
    background: "white",
    border: "1.5px solid #d4dcf8",
    borderRadius: 14,
    boxShadow: "0 8px 28px rgba(37,99,235,0.12)",
    marginTop: 6,
    overflow: "hidden",
    maxHeight: 260,
    overflowY: "auto",
  },
  aiItem: {
    padding: "12px 16px",
    cursor: "pointer",
    borderBottom: "1px solid #f0f2fa",
    fontSize: 14,
    fontWeight: 700,
    color: "#1a2b5e",
    transition: "background 0.12s",
  },
  spinner: {
    display: "inline-block",
    width: 16,
    height: 16,
    border: "2.5px solid rgba(255,255,255,0.4)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  },
};

const OPTION_LABELS = ["A", "B", "C", "D"];
const MAX_QUESTION_CHARS = 100;
const MAX_OPTION_CHARS = 200;
const MAX_ANSWER_CHARS = 200;
const MAX_QUESTIONS_ALLOWED = 20;

function clampText(value, maxLen) {
  return String(value ?? "").slice(0, maxLen);
}

function newQuestion(overrides = {}) {
  return {
    id: Date.now() + Math.random(),
    question: "",
    answer: "",
    hints: 4,
    options: ["", "", "", ""],
    expanded: true,
    ...overrides,
  };
}

function parseAiResponse(responseText) {
  const clean = String(responseText || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .trim();

  if (!clean) throw new Error("AI returned empty response.");

  const tryParse = (candidate) => {
    try { return JSON.parse(candidate); } catch { return null; }
  };

  const direct = tryParse(clean);
  if (Array.isArray(direct)) return direct;
  if (Array.isArray(direct?.questions)) return direct.questions;

  const firstBracket = clean.indexOf("[");
  const lastBracket = clean.lastIndexOf("]");
  if (firstBracket !== -1 && lastBracket !== -1 && lastBracket > firstBracket) {
    const extracted = tryParse(clean.slice(firstBracket, lastBracket + 1));
    if (Array.isArray(extracted)) return extracted;
  }

  throw new Error("AI returned invalid format.");
}

// ── Step 1: ask for ONLY a list of real, distinct answers — no hints yet.
// A small model is much more reliable at recalling names than at generating
// name + 4 unique matching facts in one shot, which is what caused mismatched
// facts (e.g. the wrong sport attached to an athlete, or non-answers like a
// head of state showing up under "Olympic winners").
function buildAnswerListPrompt({ topic, count, excludeAnswers = [] }) {
  const excluded = Array.isArray(excludeAnswers)
    ? excludeAnswers.map((a) => String(a || "").trim()).filter(Boolean)
    : [];

  return `List up to ${count} different, real, well-known examples that DIRECTLY and UNQUESTIONABLY satisfy the topic "${topic}".

STRICT RULES:
- Only include an entry if you are 100% certain it is a genuine, well-known example of "${topic}" itself — not something merely related, associated, or adjacent to it.
- Do NOT include founders, organizers, coaches, relatives, sponsors, commentators, inventors, or anyone/anything that does not literally and directly satisfy "${topic}" (for example, for "Olympic medal winners": only people who themselves won an Olympic medal — never a founder of the Olympics, a coach, a relative of a winner, or an unrelated celebrity).
- Prefer only the most famous, mainstream, universally recognized examples. Avoid obscure, minor, or uncertain picks.
- If you cannot think of ${count} entries you are fully certain about, return FEWER — it is much better to return fewer correct entries than to guess and include a wrong one.
- All entries must be different from each other.
- Pick a varied mix rather than only the single most obvious/iconic names — randomize your selection and ordering each time you answer this, while still only including entries you are fully certain about.
${excluded.length ? `- Do not use any of these (already used): ${excluded.join(", ")}` : ""}

Respond with ONLY a JSON array of strings (at most ${count} items), nothing else. Example: ["Answer One","Answer Two"]`;
}

// ── Step 1.5: verify each candidate actually, directly satisfies the topic ─
// Step 1 still occasionally names something merely *related* to the topic
// (e.g. a founder/coach/relative instead of an actual winner). A focused
// yes/no check per name is a much easier, more reliable task for a small
// model than open recall, and catches most of those mis-fits before we
// waste a hint-generation call — or worse, show the wrong answer to a player.
async function verifyAnswerFitsTopic({ topic, answer, token }) {
  const prompt = `Topic: "${topic}"
Candidate: "${answer}"

Does "${answer}" genuinely and directly belong to "${topic}"?

- Fame level does NOT matter — a lesser-known but real, working example still counts as YES. Do not say NO just because "${answer}" is not one of the biggest superstars.
- Say NO only if "${answer}" does not actually satisfy "${topic}" at all — e.g. they work in a completely different field, or they are merely related/adjacent to it without doing it themselves (a founder, organizer, coach, relative, sponsor, or similar — not the thing itself).
- If "${answer}" truly is a real example of "${topic}", answer YES even if you don't know much else about them.

Answer with exactly one word: YES or NO.`;
  const raw = await callAI(prompt, token);
  const clean = String(raw || "").trim().toUpperCase();
  return clean.startsWith("YES");
}

// ── Step 2: for ONE already-chosen answer, ask for 4 accurate progressive hints.
// Anchoring the model to a single given name (instead of inventing name + facts
// together) sharply cuts down on hallucinated/mismatched details.
function buildHintsPrompt({ topic, answer }) {
  return `The correct answer is: "${answer}" — an item from the topic "${topic}".

Write exactly 4 short clues about "${answer}" for a guessing game, ordered from vague to almost-revealing.

Rules:
- Every clue must be a true, well-known fact specifically about "${answer}" — never guess, assume, or invent a detail.
- If you are not fully certain a specific fact (birthplace, exact record, date, relationship, statistic, etc.) is correct, use a safer, more general true statement instead.
- All 4 clues must be about "${answer}" only — do not mix in facts about anyone/anything else.
- Do not write the name "${answer}" (or an obvious part of it) inside any clue.
- Do not use generic filler like "I am famous" or "I am well known".
- option_four can be strong and specific but must still not state the answer's name.
- Keep each clue under ${MAX_OPTION_CHARS} characters.

Respond with ONLY a JSON object with exactly these 4 fields, nothing else:
{"option_one":"...","option_two":"...","option_three":"...","option_four":"..."}`;
}

// ── Step 2.5: verify the 4 hints are consistent, true facts about the SAME
// answer. A verified-real name can still get invented/contradictory hints
// (e.g. calling a shot-put gold medalist a "sprinter" with a "sub-100m
// distance" in one clue and a "field event" in another) — this catches
// internal contradictions and low-confidence invented specifics.
async function verifyHintsConsistency({ answer, hints, token }) {
  const prompt = `Candidate answer: "${answer}"
Clues:
1. ${hints.option_one}
2. ${hints.option_two}
3. ${hints.option_three}
4. ${hints.option_four}

Are ALL 4 clues true, specific, and consistent facts about "${answer}" — describing the SAME single field/sport/achievement, with no contradiction between clues and no invented or uncertain detail (for example, never describe the same answer as both a sprint/running event and a throwing/field event)?

Answer with exactly one word: YES or NO.`;
  const raw = await callAI(prompt, token);
  const clean = String(raw || "").trim().toUpperCase();
  return clean.startsWith("YES");
}

function parseAiObject(responseText) {
  const clean = String(responseText || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .trim();

  if (!clean) return null;

  const tryParse = (candidate) => {
    try { return JSON.parse(candidate); } catch { return null; }
  };

  const direct = tryParse(clean);
  if (direct && typeof direct === "object" && !Array.isArray(direct)) return direct;

  const firstBrace = clean.indexOf("{");
  const lastBrace = clean.lastIndexOf("}");
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    const extracted = tryParse(clean.slice(firstBrace, lastBrace + 1));
    if (extracted && typeof extracted === "object" && !Array.isArray(extracted)) return extracted;
  }

  return null;
}
// ── Max questions per single AI call — never ask for more than this ───────────
const AI_BATCH_SIZE = 10;

// ── Proxy AI generate requests through the backend so the API key/URL stay server-side ─
// `options` (temperature/top_p/seed) is forwarded to the model. Without it the
// model samples greedily, so the exact same prompt always returns the exact
// same completion — that's why repeated "Generate" clicks kept surfacing the
// identical Bollywood-actors list in the identical order.
async function callAI(prompt, token, options) {
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  if (!backendBase) throw new Error("Backend URL is not configured.");
  if (!token) throw new Error("Auth token missing.");

  const resp = await fetch(`${backendBase}/generateAi`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(options ? { prompt, options } : { prompt }),
  });
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(payload?.message || "Failed to connect to AI service.");
  return payload?.response;
}

// A fresh, wide-range seed each call so repeated name-list requests don't
// collapse back to the model's single greedy top pick.
function randomSeed() {
  return Math.floor(Math.random() * 2_147_483_647);
}

// ── Two-step batch: (1) get a list of real answers, (2) get accurate hints per answer ─
// Doing this in one shot made the model invent/mix facts (wrong sport for an
// athlete, non-answers like a head of state, etc). Splitting it means the model
// only ever has to do one easy task at a time: "name real things" then
// "describe this one specific, given thing" — each far more reliable for a
// small local model than doing both together.
async function fetchAiBatch({ topic, count, excludeAnswers = [], token }) {
  // Never ask AI for more than AI_BATCH_SIZE in one call
  const safeCount = Math.min(count, AI_BATCH_SIZE);
  // Always ask the model to brainstorm a fuller list than strictly needed,
  // even when only 1-2 more answers are required. Asking for "just 1 more
  // name" round after round starves the model of brainstorming room and it
  // tends to loop on the same couple of next-best guesses (which then also
  // keep failing verification) instead of surfacing real variety. A wider
  // ask per round means more independent shots at finding enough that pass.
  const askCount = Math.min(AI_BATCH_SIZE, Math.max(safeCount, 6));

  const excludeSet = new Set(
    excludeAnswers.map((a) => String(a || "").trim().toLowerCase()).filter(Boolean)
  );

  // Step 1 — names only. Raised temperature + a fresh random seed so this
  // doesn't collapse to the same greedy top-N list on every call.
  const listRaw = await callAI(
    buildAnswerListPrompt({ topic, count: askCount, excludeAnswers }),
    token,
    { temperature: 1.0, top_p: 0.95, seed: randomSeed() }
  );
  const parsedList = parseAiResponse(listRaw);

  const candidates = [];
  if (Array.isArray(parsedList)) {
    for (const item of parsedList) {
      const name = clampText(String(typeof item === "string" ? item : item?.correct_answer || "").trim(), MAX_ANSWER_CHARS);
      const key = name.toLowerCase();
      if (!name || excludeSet.has(key)) continue;
      excludeSet.add(key);
      candidates.push(name);
      if (candidates.length >= askCount) break;
    }
  }

  // Step 1.5 — verify each candidate directly fits the topic before trusting it
  const answers = [];
  for (const candidate of candidates) {
    try {
      const fits = await verifyAnswerFitsTopic({ topic, answer: candidate, token });
      if (fits) answers.push(candidate);
    } catch (e) {
      console.error(`[AI] failed to verify "${candidate}":`, e?.message || e);
    }
  }

  // Step 2 — accurate hints for each verified answer, one at a time
  const unique = [];
  for (const answer of answers) {
    try {
      const hintsRaw = await callAI(buildHintsPrompt({ topic, answer }), token);
      const hints = parseAiObject(hintsRaw);
      if (!hints) continue;

      const q = {
        question_name: "Guess what I am!",
        option_one: clampText(String(hints?.option_one || "").trim(), MAX_OPTION_CHARS),
        option_two: clampText(String(hints?.option_two || "").trim(), MAX_OPTION_CHARS),
        option_three: clampText(String(hints?.option_three || "").trim(), MAX_OPTION_CHARS),
        option_four: clampText(String(hints?.option_four || "").trim(), MAX_OPTION_CHARS),
        correct_answer: answer,
        answer_id: 4,
      };
      if (!(q.option_one && q.option_two && q.option_three && q.option_four)) continue;

      const consistent = await verifyHintsConsistency({ answer, hints: q, token });
      if (consistent) unique.push(q);
    } catch (e) {
      console.error(`[AI] failed to fetch hints for "${answer}":`, e?.message || e);
    }
  }

  // Return every candidate this batch actually looked at — accepted AND
  // rejected — so the caller can exclude rejected ones from future rounds
  // too. Without this, a name that just failed verification (e.g. "Serena
  // Williams" for "Olympic winners") keeps getting re-suggested and
  // re-rejected on every subsequent round/click, wasting calls and making it
  // look like the AI never gets new answers.
  return { results: unique, tried: candidates };
}

// ── Multi-batch fetcher: splits any count >10 into sequential batches of 10 ──
// e.g. need 20 → batch1(10) then batch2(10), each excludes previous answers
//
// Verification (Step 1.5 in fetchAiBatch) now rejects candidates that don't
// truly fit the topic, so a single round frequently comes back short of the
// requested count on purpose — that's correctness working as intended, not a
// failure. We keep asking for the remainder over a few more rounds instead of
// giving up on the first short batch, bounded so a genuinely exhausted topic
// still terminates instead of looping forever.
async function fetchAiQuestions({ topic, count, excludeAnswers = [], token }) {
  const allResults = [];
  const allTried = [];
  // Running exclude set — grows as each batch completes. Includes rejected
  // candidates too (not just accepted ones) so we never re-ask about a name
  // the AI already looked at and turned down within this run.
  const runningExclude = [...excludeAnswers];
  let remaining = count;
  // Generous round budget — "Regenerate remaining" should genuinely keep
  // trying until it fills the request, not give up after a handful of
  // rejected rounds. Each round can yield as few as 0-1 verified answers, so
  // this needs real headroom, not just a small multiple of count.
  const maxRounds = Math.max(12, count * 4);
  // Still need a circuit breaker: if many rounds in a row produce zero
  // verified answers even though the AI is naming candidates, the topic is
  // genuinely exhausted (or too narrow) — stop instead of hammering the AI
  // indefinitely.
  const maxConsecutiveEmptyRounds = 6;
  let consecutiveEmptyRounds = 0;
  let round = 0;

  while (remaining > 0 && round < maxRounds) {
    round++;
    const batchSize = Math.min(remaining, AI_BATCH_SIZE);
    const { results, tried } = await fetchAiBatch({
      topic,
      count: batchSize,
      excludeAnswers: runningExclude,
      token,
    });

    allTried.push(...tried);
    runningExclude.push(...tried); // exclude both accepted and rejected names going forward

    if (results.length === 0) {
      if (tried.length === 0) break; // AI gave us nothing at all this round — stop to avoid hammering it forever
      consecutiveEmptyRounds++;
      if (consecutiveEmptyRounds >= maxConsecutiveEmptyRounds) break; // topic genuinely exhausted
      continue; // this round found candidates but none verified — try again with them excluded
    }

    consecutiveEmptyRounds = 0;
    allResults.push(...results);
    remaining -= results.length;
  }

  return { results: allResults, tried: allTried };
}

function aiToQuestion(ai) {
  return newQuestion({
    question: ai.question_name,
    answer: ai.correct_answer,
    options: [ai.option_one, ai.option_two, ai.option_three, ai.option_four],
    hints: 4,
    expanded: true,
  });
}

function QuestionRow({ q, index, onToggle, onEdit, onChange, onDelete, isMobile, isEditing }) {
  const [local, setLocal] = useState(q);

  useEffect(() => {
    setLocal(q);
  }, [q]);

  const update = (field, val) => {
    const capped =
      field === "question"
        ? clampText(val, MAX_QUESTION_CHARS)
        : field === "answer"
          ? clampText(val, MAX_ANSWER_CHARS)
          : val;
    const next = { ...local, [field]: capped };
    setLocal(next);
    onChange(next);
  };

  const updateOption = (i, val) => {
    const opts = [...local.options];
    opts[i] = clampText(val, MAX_OPTION_CHARS);
    const next = { ...local, options: opts };
    setLocal(next);
    onChange(next);
  };

  return (
    <div style={S.dropdownCard(isMobile)}>
      <div style={S.dropdownHeader(isMobile)} onClick={() => onToggle(q.id)}>
        <div style={S.qBadge(isMobile)}>Q{index + 1}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...S.qTitle(isMobile), whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {local.answer ? `Answer: ${local.answer}` : `Question ${index + 1}`}
          </div>
          <div style={S.qSub}>{local.hints} hints</div>
        </div>
        <div style={S.qActions} onClick={(e) => e.stopPropagation()}>
          <button style={S.iconBtn("#6b7db3", isEditing)} title="Edit" onClick={() => onEdit(q.id)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button style={S.iconBtn("#ef4444")} title="Delete" onClick={() => onDelete(q.id)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4h6v2" />
            </svg>
          </button>
          <span style={{
            color: "#9aa5c4",
            fontSize: 18,
            lineHeight: 1,
            transform: q.expanded ? "rotate(90deg)" : "none",
            transition: "transform 0.2s",
          }}>›</span>
        </div>
      </div>

      {q.expanded && (
        <div style={isMobile ? S.expandBodyMobile : S.expandBody}>
          <input
            style={{ ...S.qInput(isMobile), opacity: isEditing ? 1 : 0.7, cursor: isEditing ? "text" : "not-allowed" }}
            value={local.question}
            placeholder="Enter question text…"
            readOnly={!isEditing}
            maxLength={MAX_QUESTION_CHARS}
            onChange={(e) => update("question", e.target.value)}
          />
          <input
            style={{ ...S.qInput(isMobile), marginTop: 8, opacity: isEditing ? 1 : 0.7, cursor: isEditing ? "text" : "not-allowed" }}
            value={local.answer}
            placeholder="Correct answer…"
            readOnly={!isEditing}
            maxLength={MAX_ANSWER_CHARS}
            onChange={(e) => update("answer", e.target.value)}
          />
          <div style={S.optionsGrid(isMobile)}>
            {local.options.map((opt, i) => (
              <div key={i} style={S.optionRow}>
                <div style={S.optLabel}>{OPTION_LABELS[i]}</div>
                <input
                  style={{ ...S.optInput, opacity: isEditing ? 1 : 0.7, cursor: isEditing ? "text" : "not-allowed" }}
                  value={opt}
                  placeholder={`Option ${OPTION_LABELS[i]}`}
                  readOnly={!isEditing}
                  maxLength={MAX_OPTION_CHARS}
                  onChange={(e) => updateOption(i, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const isMobile = useIsMobile();

  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiResults, setAiResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [shuffle, setShuffle] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [hasHydratedQuestions, setHasHydratedQuestions] = useState(false);
  const [questionLimit, setQuestionLimit] = useState(10);
  const maxQuestions = Math.max(1, Math.min(MAX_QUESTIONS_ALLOWED, Number(questionLimit) || 10));
  const limitReached = questions.length >= maxQuestions;
  const [regenBaseCount, setRegenBaseCount] = useState(0);
  const [regenAfterDelete, setRegenAfterDelete] = useState(false);
  const [lobbyName, setLobbyName] = useState("");
  const [themeName, setThemeName] = useState("guess_me");
  const dropRef = useRef(null);

  // Names the AI has already been asked about for the current topic —
  // accepted AND rejected — so repeat generate/regenerate clicks don't keep
  // re-suggesting (and re-rejecting) the same candidates. Reset whenever the
  // topic text changes since it no longer applies.
  const triedAnswersRef = useRef([]);
  const triedTopicRef = useRef("");
  if (triedTopicRef.current !== topic) {
    triedTopicRef.current = topic;
    triedAnswersRef.current = [];
  }

  const storedUserData = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData") || "{}";
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl("/configure-game-settings"));
  }, [dispatch]);

  const isAnchorMode = useMemo(() => {
    if (location?.state?.isAnchorMode) return true;
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
      return cached?.isAnchorMode === true;
    } catch {
      return false;
    }
  }, [location?.state?.isAnchorMode]);

  const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, storedUserData]);

  useEffect(() => {
    document.documentElement.classList.add("manager-static-bg");
    document.body.classList.add("manager-static-bg");
    return () => {
      document.documentElement.classList.remove("manager-static-bg");
      document.body.classList.remove("manager-static-bg");
    };
  }, []);

  useEffect(() => {
    if (!tokenForApi || !uid) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getManagerSettings({ token: tokenForApi, userId: uid, themeName });
        if (cancelled) return;
        const qn = resp?.questions_no != null ? Number(resp.questions_no) : NaN;
        if (Number.isFinite(qn) && qn > 0) setQuestionLimit(Math.min(MAX_QUESTIONS_ALLOWED, qn));
        if (resp?.lobby_name) setLobbyName(String(resp.lobby_name));
        if (resp?.themename) setThemeName(String(resp.themename));
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken, uid, themeName]);

  useEffect(() => {
    if (!tokenForApi || !uid || !lobbyName || hasHydratedQuestions) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getLobbyGameData({
          token: tokenForApi,
          userId: uid,
          themeName,
          lobby_name: lobbyName,
        });
        if (cancelled) return;
        const rows = Array.isArray(resp?.questions) ? resp.questions : [];
        if (rows.length > 0) {
          const hydrated = rows.map((row) => {
            const options = [
              clampText(String(row?.option_one || "").trim(), MAX_OPTION_CHARS),
              clampText(String(row?.option_two || "").trim(), MAX_OPTION_CHARS),
              clampText(String(row?.option_three || "").trim(), MAX_OPTION_CHARS),
              clampText(String(row?.option_four || "").trim(), MAX_OPTION_CHARS),
            ];
            return newQuestion({
              question: clampText(String(row?.question_name || "").trim(), MAX_QUESTION_CHARS),
              answer: clampText(String(row?.correct_answer || "").trim(), MAX_ANSWER_CHARS),
              options,
              hints: options.filter(Boolean).length || 4,
              expanded: false,
            });
          });
          setQuestions(hydrated);
          setEditingQuestionId(null);
          setRegenBaseCount(hydrated.length);
          setRegenAfterDelete(false);
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) setHasHydratedQuestions(true);
      }
    })();
    return () => { cancelled = true; };
  }, [tokenForApi, uid, lobbyName, themeName, hasHydratedQuestions]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    if (limitReached) return;

    // How many slots are left — guard BEFORE spinner starts
    const count = Math.max(0, maxQuestions - questions.length);
    if (count <= 0) return;

    setLoading(true);
    setShowDropdown(false);

    try {
      setRegenBaseCount(maxQuestions); // store TOTAL target for regen math
      setRegenAfterDelete(false);

      // Exclude answers already in the list, PLUS every name already tried
      // (accepted or rejected) for this topic — otherwise a rejected name
      // like "Serena Williams" for "Olympic winners" keeps getting
      // re-suggested and re-rejected on every click.
      const existingAnswers = questions.map((q) => q.answer).filter(Boolean);

      // fetchAiQuestions automatically splits into batches of 10
      // e.g. need 20 → calls AI twice: first 10, then 10 (excluding first batch answers)
      // e.g. need 7 → single call for 7
      const { results: allGenerated, tried } = await fetchAiQuestions({
        topic,
        count,
        excludeAnswers: [...existingAnswers, ...triedAnswersRef.current],
        token: tokenForApi,
      });
      triedAnswersRef.current = [...triedAnswersRef.current, ...tried];

      // Hard cap — should already be correct but belt-and-suspenders
      const finalBatch = allGenerated.slice(0, count);
      const generated = finalBatch.map(aiToQuestion);

      // APPEND to existing, hard cap at maxQuestions
      setQuestions((prev) => [...prev, ...generated].slice(0, maxQuestions));
      setAiResults(finalBatch);
      // If the AI (correctly) came back short of the requested count — e.g. it
      // ran out of answers it could verify as accurate — surface the
      // "Regenerate remaining" button so the admin can ask again instead of
      // silently ending up with fewer questions than requested.
      setRegenAfterDelete(finalBatch.length < count);
      setShowDropdown(false);

      if (finalBatch.length < count) {
        await Swal.fire(
          finalBatch.length === 0 ? "No verified answers found" : "Fewer questions than requested",
          finalBatch.length === 0
            ? `The AI couldn't verify any accurate answers for "${topic}". Try a broader or more specific topic.`
            : `Generated ${finalBatch.length} verified question${finalBatch.length === 1 ? "" : "s"} (asked for ${count}) — the AI couldn't verify more accurate answers for "${topic}" right now. Use "Regenerate remaining" to try for more.`,
          "info"
        );
      }
    } catch (e) {
      await Swal.fire("Error", e?.message || "Failed to generate questions.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateRemaining = async () => {
    const remainingCount = Math.max(0, (Number(regenBaseCount) || 0) - questions.length);
    if (!topic.trim()) return;
    if (remainingCount <= 0) return;
    if (limitReached) return;

    setLoading(true);
    setShowDropdown(false);

    try {
      // Exclude answers already in the list, PLUS every name already tried
      // (accepted or rejected) for this topic so far — same reasoning as
      // handleGenerate: don't re-ask about a name the AI already rejected.
      const exclude = questions.map((q) => q?.answer).filter(Boolean);

      const { results: batch, tried } = await fetchAiQuestions({
        topic,
        count: remainingCount,
        excludeAnswers: [...exclude, ...triedAnswersRef.current],
        token: tokenForApi,
      });
      triedAnswersRef.current = [...triedAnswersRef.current, ...tried];

      const generated = batch.map(aiToQuestion);
      const addedCount = generated.length;

      setQuestions((prev) => {
        const room = Math.max(0, maxQuestions - prev.length);
        if (room <= 0) return prev;
        return [...prev, ...generated.slice(0, room)];
      });
      setAiResults((prev) => [...prev, ...batch]);

      const newTotal = questions.length + addedCount;
      // Keep the button visible if we're still short — verification can
      // legitimately reject every candidate in a round (topic exhausted for
      // now), which used to silently hide the button with zero explanation.
      setRegenAfterDelete(newTotal < regenBaseCount);
      setShowDropdown(false);

      if (addedCount === 0) {
        await Swal.fire(
          "No new questions found",
          `The AI couldn't verify any additional accurate answers for "${topic}". Try a broader topic, or add the rest manually.`,
          "info"
        );
      } else if (addedCount < remainingCount) {
        await Swal.fire(
          "Added fewer than requested",
          `Added ${addedCount} verified question${addedCount === 1 ? "" : "s"} (asked for ${remainingCount}) — the AI couldn't verify more accurate answers for "${topic}" right now.`,
          "info"
        );
      }
    } catch (e) {
      await Swal.fire("Error", e?.message || "Failed to regenerate questions.", "error");
    } finally {
      setLoading(false);
    }
  };

  const addAiQuestion = (ai) => {
    if (limitReached) return;
    const q = aiToQuestion(ai);
    setQuestions((prev) => {
      if (prev.length >= maxQuestions) return prev;
      return [...prev, q];
    });
    setShowDropdown(false);
  };

  const addBlankQuestion = () =>
    setQuestions((prev) => {
      if (prev.length >= maxQuestions) return prev;
      return [...prev, newQuestion()];
    });

  const toggleExpand = (id) =>
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== id) return q;
        const nextExpanded = !q.expanded;
        if (!nextExpanded && editingQuestionId === id) setEditingQuestionId(null);
        return { ...q, expanded: nextExpanded };
      })
    );

  const editQuestion = (id) => {
    setEditingQuestionId(id);
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, expanded: true } : q)));
  };

  const updateQuestion = (updated) =>
    setQuestions((prev) => prev.map((q) => (q.id === updated.id ? { ...updated } : q)));

  const deleteQuestion = (id) => {
    if (editingQuestionId === id) setEditingQuestionId(null);
    setRegenAfterDelete(true);
    setQuestions((prev) => prev.filter((q) => q.id !== id));
  };

  const handleStartGame = async () => {
    if (!tokenForApi || !uid || !lobbyName) {
      Swal.fire("Error", "Missing token/user/lobby. Please go back and configure the lobby first.", "error");
      return;
    }

    const payloadQuestions = questions
      .map((q) => ({
        question_name: clampText(String(q.question || "").trim(), MAX_QUESTION_CHARS),
        option_one: clampText(String(q.options?.[0] || "").trim(), MAX_OPTION_CHARS),
        option_two: clampText(String(q.options?.[1] || "").trim(), MAX_OPTION_CHARS),
        option_three: clampText(String(q.options?.[2] || "").trim(), MAX_OPTION_CHARS),
        option_four: clampText(String(q.options?.[3] || "").trim(), MAX_OPTION_CHARS),
        correct_answer: clampText(String(q.answer || "").trim(), MAX_ANSWER_CHARS),
        answer_id: "4",
        is_active: "true",
      }))
      .filter((q) => q.question_name && q.correct_answer);

    if (!payloadQuestions.length) {
      Swal.fire("Error", "No questions to save. Please add at least one question.", "error");
      return;
    }

    // Block starting the game short-handed — e.g. AI generated 9 of 10 and
    // verification correctly withheld the 10th; starting anyway would launch
    // a lobby with fewer questions than the manager configured.
    if (payloadQuestions.length < maxQuestions) {
      const short = maxQuestions - payloadQuestions.length;
      Swal.fire(
        "More questions needed",
        `This lobby needs ${maxQuestions} questions but only ${payloadQuestions.length} ${payloadQuestions.length === 1 ? "is" : "are"} ready (${short} short). Use "Regenerate remaining" to have the AI fill in the rest, or add ${short} more manually before starting.`,
        "warning"
      );
      return;
    }

    try {
      const isGuest = deriveGuestFromUser(storedUserData);
      const gameId = String(storedUserData.gameId ?? "").trim();
      const sessionId = String(storedUserData.sessionId ?? "").trim();
      const organizationId = String(storedUserData.organizationId ?? "").trim();

      let joinLink = "";
      if (gameId && sessionId && organizationId) {
        const base = buildExtramileJoinUrl(isGuest, gameId, sessionId, organizationId);
        joinLink = withQuestionAmp(appendLobbyShareFlagsToJoinUrl(base, lobbyName));
      } else {
        joinLink = buildFallbackAppLink(lobbyName);
      }

      try {
        await updateGameSettings({
          token: tokenForApi,
          userId: uid,
          themeName,
          current_mode: "Manager",
          shuffle_questions: shuffle,
          lobby_link: joinLink || null,
          email: storedUserData?.email ?? null,
          fullName: storedUserData?.name ?? storedUserData?.fullName ?? null,
          employeeId: storedUserData?.employeeId ?? null,
          reportid: storedUserData?.reportId ?? null,
        });
      } catch {
        // ignore
      }

      const res = await addManagerQuestions({
        token: tokenForApi,
        userId: uid,
        themename: themeName,
        lobby_name: lobbyName,
        questions: payloadQuestions,
      });

      await Swal.fire("Saved!", `Questions saved: ${res?.inserted ?? payloadQuestions.length}`, "success");

      if (isAnchorMode) {
        navigate("/game", { replace: true, state: { lobby_name: lobbyName, isAnchorMode: true } });
      } else {
        navigate("/usergame", { replace: true, state: { lobby_name: lobbyName } });
      }
    } catch (e) {
      await Swal.fire("Error", e?.message || "Failed to save questions.", "error");
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
        @keyframes fadeIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Nunito', sans-serif; }
        input:focus { border-color: #4f9ef8 !important; box-shadow: 0 0 0 3px rgba(79,158,248,0.15); }
        ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-thumb { background: #d4dcf8; border-radius: 6px; }
      `}</style>

      <div style={S.page(isMobile)}>
        <div style={S.bgBlob1} />
        <div style={S.bgBlob2} />
        {!isMobile && (
          <div style={S.qmarks}>
            {["?", "?", "?", "?"].map((c, i) => (
              <span key={i} style={S.qmark}>{c}</span>
            ))}
          </div>
        )}

        <div style={S.inner}>
          <h1 style={S.title(isMobile)}>Manager Dashboard</h1>

          {/* Top row: Generate + Settings cards */}
          <div style={S.topRow(isMobile)}>
            <div style={{ ...S.card(isMobile), ...S.genCard(isMobile) }}>
              <div style={S.cardTitle(isMobile)}>Generate Questions</div>
              <div style={{ position: "relative" }} ref={dropRef}>
                <input
                  style={S.topicInput(isMobile)}
                  value={topic}
                  placeholder="Enter topic (e.g. Animals, Movies…)"
                  onChange={(e) => setTopic(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
                />
                {showDropdown && aiResults.length > 0 && (
                  <div style={S.aiDropdown}>
                    {aiResults.map((ai, i) => (
                      <div
                        key={i}
                        style={S.aiItem}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#eef2ff")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
                        onClick={() => addAiQuestion(ai)}
                      >
                        <div style={{ fontSize: 13, color: "#2563eb", marginBottom: 2, fontWeight: 700 }}>
                          Answer: {ai.correct_answer}
                        </div>
                        <div style={{ fontSize: 13, color: "#4b5e8a", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {ai.question_name}
                        </div>
                        <div style={{ fontSize: 11, color: "#9aa5c4", marginTop: 3 }}>
                          {[ai.option_one, ai.option_two, ai.option_three, ai.option_four].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                style={{ ...S.genBtn(isMobile), opacity: loading || limitReached ? 0.6 : 1, cursor: loading || limitReached ? "not-allowed" : "pointer" }}
                onClick={handleGenerate}
                disabled={loading || limitReached}
              >
                {loading ? (
                  <><span style={S.spinner} /> Generating…</>
                ) : (
                  <><span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Generate</>
                )}
              </button>
            </div>

            <div style={{ ...S.card(isMobile), ...S.settCard(isMobile) }}>
              <div style={S.cardTitle(isMobile)}>Settings</div>
              <div style={S.checkRow} onClick={() => setShuffle((v) => !v)}>
                <div style={S.checkBox(shuffle)}>
                  {shuffle && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <span style={S.checkLabel}>Shuffle Questions</span>
              </div>
            </div>
          </div>

          {/* Questions header */}
          <div style={S.sectionLabel(isMobile)}>Questions ({questions.length}/{maxQuestions})</div>

          {/* Regenerate remaining button — only shown after a manual delete */}
          {regenAfterDelete && Math.max(0, (Number(regenBaseCount) || 0) - questions.length) > 0 && (
            <div style={{ marginBottom: 14 }}>
              <button
                type="button"
                style={{ ...S.genBtn(isMobile), maxWidth: 360, marginInline: "auto", opacity: loading ? 0.75 : 1 }}
                onClick={handleRegenerateRemaining}
                disabled={loading || !topic.trim() || limitReached}
              >
                {loading ? (
                  <><span style={S.spinner} /> Regenerating…</>
                ) : (
                  <>Regenerate remaining {Math.max(0, (Number(regenBaseCount) || 0) - questions.length)}</>
                )}
              </button>
            </div>
          )}

          {/* Question list */}
          {questions.map((q, i) => (
            <QuestionRow
              key={q.id}
              q={q}
              index={i}
              isMobile={isMobile}
              onToggle={toggleExpand}
              onEdit={editQuestion}
              onChange={updateQuestion}
              onDelete={deleteQuestion}
              isEditing={editingQuestionId === q.id}
            />
          ))}

          <button
            style={S.addNewBtn(isMobile)}
            onClick={addBlankQuestion}
            disabled={limitReached}
            onMouseEnter={(e) => {
              if (limitReached) return;
              e.currentTarget.style.background = "rgba(255,255,255,0.85)";
              e.currentTarget.style.borderColor = "#4f9ef8";
              e.currentTarget.style.color = "#2563eb";
            }}
            onMouseLeave={(e) => {
              if (limitReached) return;
              e.currentTarget.style.background = "rgba(255,255,255,0.5)";
              e.currentTarget.style.borderColor = "#b8c8f0";
              e.currentTarget.style.color = "#6b7db3";
            }}
          >
            {limitReached ? "Question limit reached" : "+ Add New Question"}
          </button>

          <button style={S.startBtn(isMobile)} onClick={handleStartGame}>
            Start Game
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </button>
        </div>

        <button
          style={{ ...S.fab, opacity: limitReached ? 0.5 : 1, cursor: limitReached ? "not-allowed" : "pointer" }}
          onClick={addBlankQuestion}
          title={limitReached ? "Question limit reached" : "Add Question"}
          disabled={limitReached}
        >
          +
        </button>
      </div>
    </>
  );
}
